import { produce } from 'immer';
import { StateConfig, StateMachine, StateSchema, assign, createMachine } from 'xstate';

import { BUY_TYPE } from 'src/checkout/templates/Checkout/Forms/CheckoutForm.types';

import { PersonalisedQuotePayloadPartial } from './templates/Forms/PersonalizedQuoteForm.type';

export const enum MACHINE_EVENT {
  NEXT = 'NEXT',
  BACK = 'BACK',
  LOGIN = 'LOGIN',
  SEARCH_FOR_ACCOUNT = 'SEARCH_FOR_ACCOUNT',
  RESET_PASSWORD = 'RESET_PASSWORD',
}

const enum MACHINE_ACTION {
  ADD_VALUES = 'addValues',
  SET_BACK_TRANSITION = 'setBackTransition',
  SET_FORWARD_TRANSITION = 'setForwardTransition',
  SET_LOGIN = 'setLogin',
}

export const enum MACHINE_STATE {
  START = 'start',
  LOAN_TYPE = 'loanType',
  EMAIL = 'email',
  PHONE = 'phone',
  SIGN_IN = 'signIn',
  RESET_PASSWORD = 'resetPassword',
  RESEND_PASSWORD = 'resendPassword',
  PERSONAL_DETAILS = 'personalDetails',
  FAMILY_AND_RESIDENTIAL = 'familyAndResidential',
  EMPLOYMENT_AND_CITIZENSHIP = 'employmentAndCitizenship',
  TERMS_AND_CONDITIONS = 'termsAndConditions',
  CREATE_ACCOUNT = 'createAccount',
  VERIFY_ACCOUNT = 'verifyAccount',
  FINANCE_QUOTES_LIST = 'financeQuotesList',
  CONFIRMATION_PAGE = 'confirmation',
  END = 'end',
}

const enum MACHINE_GUARD {
  IS_EXISTING_ACCOUNT = 'isExistingAccount',
  IS_LOGGED_IN = 'isLoggedIn',
}

export interface PersonalisedQuoteMachineContext {
  isBackTransition: boolean;
  payload: PersonalisedQuotePayloadPartial;
  isLoggedIn: boolean;
  isExistingAccount: boolean;
}

type MachineEvent =
  | {
      type: MACHINE_EVENT.NEXT;
      payload: PersonalisedQuotePayloadPartial;
    }
  | {
      type: MACHINE_EVENT.BACK;
    }
  | {
      type: MACHINE_EVENT.LOGIN;
    }
  | {
      type: MACHINE_EVENT.SEARCH_FOR_ACCOUNT;
      isExistingAccount: boolean;
    }
  | {
      type: MACHINE_EVENT.RESET_PASSWORD;
    };

type MachineContextAllDefined = Modify<
  PersonalisedQuoteMachineContext,
  { loanType: NoUndefined<PersonalisedQuoteMachineContext['payload']['loanType']> }
>;

type TypeState<Value, Context> = {
  value: Value;
  context: Context;
};

type MachineTypestate =
  | TypeState<MACHINE_STATE.START | MACHINE_STATE.LOAN_TYPE, PersonalisedQuoteMachineContext>
  | TypeState<
      | MACHINE_STATE.PERSONAL_DETAILS
      | MACHINE_STATE.EMAIL
      | MACHINE_STATE.PHONE
      | MACHINE_STATE.SIGN_IN
      | MACHINE_STATE.RESET_PASSWORD
      | MACHINE_STATE.RESEND_PASSWORD
      | MACHINE_STATE.FAMILY_AND_RESIDENTIAL
      | MACHINE_STATE.EMPLOYMENT_AND_CITIZENSHIP
      | MACHINE_STATE.TERMS_AND_CONDITIONS
      | MACHINE_STATE.CREATE_ACCOUNT
      | MACHINE_STATE.VERIFY_ACCOUNT
      | MACHINE_STATE.FINANCE_QUOTES_LIST
      | MACHINE_STATE.CONFIRMATION_PAGE,
      MachineContextAllDefined
    >;

export type PersonalisedQuoteMachineInitialState = StateConfig<PersonalisedQuoteMachineContext, MachineEvent>;

export type PersonalisedQuoteMachine = StateMachine<
  PersonalisedQuoteMachineContext,
  StateSchema<PersonalisedQuoteMachineContext>,
  MachineEvent,
  MachineTypestate
>;

export const personalisedQuoteMachine: PersonalisedQuoteMachine = createMachine<
  PersonalisedQuoteMachineContext,
  MachineEvent,
  MachineTypestate
>(
  {
    initial: MACHINE_STATE.START,
    predictableActionArguments: true,
    context: {
      payload: {},
      isBackTransition: false,
      isLoggedIn: false,
      isExistingAccount: false,
    },
    states: {
      [MACHINE_STATE.START]: {
        on: {
          [MACHINE_EVENT.NEXT]: [
            {
              target: MACHINE_STATE.LOAN_TYPE,
              actions: [MACHINE_ACTION.ADD_VALUES, MACHINE_ACTION.SET_FORWARD_TRANSITION],
            },
          ],
        },
      },
      [MACHINE_STATE.LOAN_TYPE]: {
        on: {
          [MACHINE_EVENT.BACK]: {
            target: MACHINE_STATE.START,
            actions: [MACHINE_ACTION.SET_BACK_TRANSITION],
          },
          [MACHINE_EVENT.NEXT]: [
            {
              target: MACHINE_STATE.PERSONAL_DETAILS,
              cond: MACHINE_GUARD.IS_LOGGED_IN,
              actions: [MACHINE_ACTION.ADD_VALUES, MACHINE_ACTION.SET_FORWARD_TRANSITION],
            },
            {
              target: MACHINE_STATE.EMAIL,
              actions: [MACHINE_ACTION.ADD_VALUES, MACHINE_ACTION.SET_FORWARD_TRANSITION],
            },
          ],
        },
      },
      [MACHINE_STATE.EMAIL]: {
        on: {
          [MACHINE_EVENT.BACK]: {
            target: MACHINE_STATE.LOAN_TYPE,
            actions: [MACHINE_ACTION.SET_BACK_TRANSITION],
          },
          [MACHINE_EVENT.NEXT]: [
            {
              target: MACHINE_STATE.SIGN_IN,
              cond: MACHINE_GUARD.IS_EXISTING_ACCOUNT,
              actions: [MACHINE_ACTION.ADD_VALUES, MACHINE_ACTION.SET_FORWARD_TRANSITION],
            },
            {
              target: MACHINE_STATE.PERSONAL_DETAILS,
              cond: MACHINE_GUARD.IS_LOGGED_IN,
              actions: [MACHINE_ACTION.ADD_VALUES, MACHINE_ACTION.SET_FORWARD_TRANSITION],
            },
            {
              target: MACHINE_STATE.PERSONAL_DETAILS,
              actions: [MACHINE_ACTION.ADD_VALUES, MACHINE_ACTION.SET_FORWARD_TRANSITION],
            },
          ],
        },
      },
      [MACHINE_STATE.SIGN_IN]: {
        on: {
          [MACHINE_EVENT.RESET_PASSWORD]: {
            target: MACHINE_STATE.RESET_PASSWORD,
            actions: [MACHINE_ACTION.ADD_VALUES, MACHINE_ACTION.SET_FORWARD_TRANSITION],
          },
          [MACHINE_EVENT.BACK]: {
            target: MACHINE_STATE.EMAIL,
            actions: [MACHINE_ACTION.SET_BACK_TRANSITION],
          },
          [MACHINE_EVENT.LOGIN]: [
            {
              target: MACHINE_STATE.PERSONAL_DETAILS,
              actions: [MACHINE_ACTION.SET_LOGIN, MACHINE_ACTION.SET_FORWARD_TRANSITION],
            },
          ],
        },
      },
      [MACHINE_STATE.RESET_PASSWORD]: {
        on: {
          [MACHINE_EVENT.BACK]: {
            target: MACHINE_STATE.SIGN_IN,
            actions: [MACHINE_ACTION.SET_BACK_TRANSITION],
          },
          [MACHINE_EVENT.NEXT]: [
            {
              target: MACHINE_STATE.RESEND_PASSWORD,
              actions: [MACHINE_ACTION.ADD_VALUES, MACHINE_ACTION.SET_FORWARD_TRANSITION],
            },
          ],
        },
      },
      [MACHINE_STATE.RESEND_PASSWORD]: {
        on: {
          [MACHINE_EVENT.BACK]: {
            target: MACHINE_STATE.RESET_PASSWORD,
            actions: [MACHINE_ACTION.SET_BACK_TRANSITION],
          },
          [MACHINE_EVENT.LOGIN]: {
            target: MACHINE_STATE.PERSONAL_DETAILS,
            actions: [MACHINE_ACTION.SET_LOGIN, MACHINE_ACTION.SET_FORWARD_TRANSITION],
          },
        },
      },
      [MACHINE_STATE.PERSONAL_DETAILS]: {
        on: {
          [MACHINE_EVENT.NEXT]: {
            target: MACHINE_STATE.FAMILY_AND_RESIDENTIAL,
            actions: [MACHINE_ACTION.ADD_VALUES, MACHINE_ACTION.SET_FORWARD_TRANSITION],
          },
          [MACHINE_EVENT.BACK]: [
            {
              target: MACHINE_STATE.LOAN_TYPE,
              cond: MACHINE_GUARD.IS_LOGGED_IN,
              actions: [MACHINE_ACTION.SET_BACK_TRANSITION],
            },
            {
              target: MACHINE_STATE.EMAIL,
              actions: [MACHINE_ACTION.SET_BACK_TRANSITION],
            },
          ],
        },
      },
      [MACHINE_STATE.FAMILY_AND_RESIDENTIAL]: {
        on: {
          [MACHINE_EVENT.NEXT]: {
            target: MACHINE_STATE.EMPLOYMENT_AND_CITIZENSHIP,
            actions: [MACHINE_ACTION.ADD_VALUES, MACHINE_ACTION.SET_FORWARD_TRANSITION],
          },
          [MACHINE_EVENT.BACK]: {
            target: MACHINE_STATE.PERSONAL_DETAILS,
            actions: [MACHINE_ACTION.SET_BACK_TRANSITION],
          },
        },
      },
      [MACHINE_STATE.EMPLOYMENT_AND_CITIZENSHIP]: {
        on: {
          [MACHINE_EVENT.NEXT]: [
            {
              target: MACHINE_STATE.TERMS_AND_CONDITIONS,
              actions: [MACHINE_ACTION.ADD_VALUES, MACHINE_ACTION.SET_FORWARD_TRANSITION],
            },
          ],
          [MACHINE_EVENT.BACK]: {
            target: MACHINE_STATE.FAMILY_AND_RESIDENTIAL,
            actions: [MACHINE_ACTION.SET_BACK_TRANSITION],
          },
        },
      },
      [MACHINE_STATE.CREATE_ACCOUNT]: {
        on: {
          [MACHINE_EVENT.NEXT]: {
            target: MACHINE_STATE.VERIFY_ACCOUNT,
            actions: [MACHINE_ACTION.ADD_VALUES, MACHINE_ACTION.SET_FORWARD_TRANSITION],
          },
          [MACHINE_EVENT.BACK]: {
            target: MACHINE_STATE.EMPLOYMENT_AND_CITIZENSHIP,
            actions: [MACHINE_ACTION.SET_BACK_TRANSITION],
          },
        },
      },
      [MACHINE_STATE.VERIFY_ACCOUNT]: {
        on: {
          [MACHINE_EVENT.NEXT]: [
            {
              target: MACHINE_STATE.FINANCE_QUOTES_LIST,
              cond: MACHINE_GUARD.IS_LOGGED_IN,
              actions: [MACHINE_ACTION.ADD_VALUES, MACHINE_ACTION.SET_FORWARD_TRANSITION],
            },
            {
              target: MACHINE_STATE.TERMS_AND_CONDITIONS,
              actions: [MACHINE_ACTION.ADD_VALUES, MACHINE_ACTION.SET_FORWARD_TRANSITION],
            },
          ],
          [MACHINE_EVENT.BACK]: {
            target: MACHINE_STATE.CREATE_ACCOUNT,
            actions: [MACHINE_ACTION.SET_BACK_TRANSITION],
          },
        },
      },
      [MACHINE_STATE.TERMS_AND_CONDITIONS]: {
        on: {
          [MACHINE_EVENT.NEXT]: [
            {
              target: MACHINE_STATE.FINANCE_QUOTES_LIST,
              cond: MACHINE_GUARD.IS_LOGGED_IN,
              actions: [MACHINE_ACTION.ADD_VALUES, MACHINE_ACTION.SET_FORWARD_TRANSITION],
            },
            {
              target: MACHINE_STATE.CREATE_ACCOUNT,
              actions: [MACHINE_ACTION.ADD_VALUES, MACHINE_ACTION.SET_FORWARD_TRANSITION],
            },
          ],
          [MACHINE_EVENT.BACK]: {
            target: MACHINE_STATE.EMPLOYMENT_AND_CITIZENSHIP,
            actions: [MACHINE_ACTION.SET_BACK_TRANSITION],
          },
        },
      },
      [MACHINE_STATE.FINANCE_QUOTES_LIST]: {
        on: {
          [MACHINE_EVENT.NEXT]: {
            target: MACHINE_STATE.CONFIRMATION_PAGE,
            actions: [MACHINE_ACTION.ADD_VALUES, MACHINE_ACTION.SET_FORWARD_TRANSITION],
          },
          [MACHINE_EVENT.BACK]: {
            target: MACHINE_STATE.TERMS_AND_CONDITIONS,
            actions: [MACHINE_ACTION.SET_BACK_TRANSITION],
          },
        },
      },
      [MACHINE_STATE.CONFIRMATION_PAGE]: {
        on: {
          [MACHINE_EVENT.NEXT]: {
            target: MACHINE_STATE.END,
            actions: [MACHINE_ACTION.SET_FORWARD_TRANSITION],
          },
          [MACHINE_EVENT.BACK]: {
            target: MACHINE_STATE.FINANCE_QUOTES_LIST,
            actions: [MACHINE_ACTION.SET_BACK_TRANSITION],
          },
        },
      },
      [MACHINE_STATE.END]: { type: 'final' },
    },
    on: {
      [MACHINE_EVENT.SEARCH_FOR_ACCOUNT]: {
        actions: assign((context, event) => ({ ...context, isExistingAccount: event.isExistingAccount })),
      },
      [MACHINE_EVENT.LOGIN]: {
        actions: MACHINE_ACTION.SET_LOGIN,
      },
    },
  },
  {
    actions: {
      [MACHINE_ACTION.ADD_VALUES]: assign((context, event) =>
        produce(context, (draft) => {
          const payload = event.type === MACHINE_EVENT.NEXT ? { ...draft.payload, ...event.payload } : draft.payload;
          draft.payload = payload;
          draft.payload.isBusinessUseSelected = payload?.loanType?.loanType === BUY_TYPE.BUSINESS;
          draft.payload.isQuoteConfirmed = !!(
            payload?.financeQuote?.quoteId?.length && payload.financeQuote.quoteId.length > 0
          );
        }),
      ),
      [MACHINE_ACTION.SET_FORWARD_TRANSITION]: assign((context) =>
        produce(context, (draft) => {
          draft.isBackTransition = false;
        }),
      ),
      [MACHINE_ACTION.SET_BACK_TRANSITION]: assign((context) =>
        produce(context, (draft) => {
          draft.isBackTransition = true;
        }),
      ),
      [MACHINE_ACTION.SET_LOGIN]: assign({ isLoggedIn: (_) => true }),
    },
    guards: {
      [MACHINE_GUARD.IS_LOGGED_IN]: (context) => context.isLoggedIn,
      [MACHINE_GUARD.IS_EXISTING_ACCOUNT]: (context) => context.isExistingAccount,
    },
  },
);
